import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { uuid } from '@/lib/utils';

export type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};

const SUPABASE_URL = "https://irtygpadfbavrqhldhzm.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlydHlncGFkZmJhdnJxaGxkaHptIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk2NDMwNzMsImV4cCI6MjA3NTIxOTA3M30.2mf_g6QMPuefZwR0bT6KI3UzgL80nob4LA_IRsghu5U";

export function useDivusAIChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    setError(null);
    
    const userMessage: Message = {
      id: uuid(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Create assistant message placeholder
    const assistantMessage: Message = {
      id: uuid(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };

    try {
      // Get auth token for the request
      const { data: { session } } = await supabase.auth.getSession();
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/divus-ai-chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      // Handle error responses
      if (!response.ok) {
        let errorMessage = 'Erro ao conectar com a IA';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If response is not JSON, use status text
          errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }

        if (response.status === 402) {
          errorMessage = 'Créditos de IA esgotados. Adicione créditos no workspace do Lovable.';
        } else if (response.status === 429) {
          errorMessage = 'Limite de requisições excedido. Tente novamente em instantes.';
        }

        throw new Error(errorMessage);
      }

      // Check if we have a readable stream
      if (!response.body) {
        throw new Error('Resposta sem stream disponível');
      }

      // Add assistant message to UI
      setMessages(prev => [...prev, assistantMessage]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          // Handle CRLF
          if (line.endsWith('\r')) line = line.slice(0, -1);
          
          // Skip empty lines and SSE comments
          if (line.startsWith(':') || line.trim() === '') continue;
          
          // Only process data lines
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          
          // Handle stream end
          if (jsonStr === '[DONE]') {
            continue;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const deltaContent = parsed.choices?.[0]?.delta?.content;
            
            if (deltaContent) {
              assistantContent += deltaContent;
              setMessages(prev => 
                prev.map(m => 
                  m.id === assistantMessage.id 
                    ? { ...m, content: assistantContent }
                    : m
                )
              );
            }
          } catch {
            // If JSON parse fails, might be partial data - put back in buffer
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // Handle any remaining buffer content
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (let line of lines) {
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (!line.startsWith('data: ') || line.trim() === '') continue;
          
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(jsonStr);
            const deltaContent = parsed.choices?.[0]?.delta?.content;
            if (deltaContent) {
              assistantContent += deltaContent;
              setMessages(prev => 
                prev.map(m => 
                  m.id === assistantMessage.id 
                    ? { ...m, content: assistantContent }
                    : m
                )
              );
            }
          } catch {
            // Ignore final parse errors
          }
        }
      }

      // Ensure message has content, or show a fallback
      if (!assistantContent.trim()) {
        setMessages(prev => 
          prev.map(m => 
            m.id === assistantMessage.id 
              ? { ...m, content: 'Desculpe, não consegui gerar uma resposta. Tente novamente.' }
              : m
          )
        );
      }

    } catch (err) {
      console.error('Divus AI Chat error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro inesperado';
      setError(errorMessage);
      
      // Add error message as assistant response
      setMessages(prev => [...prev, {
        id: uuid(),
        role: 'assistant',
        content: `Desculpe, ocorreu um erro: ${errorMessage}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
  };
}
